import { ref, computed } from 'vue'
import { queryClient } from '@/queryClient.js'
import { useQuery } from '@tanstack/vue-query'
import { useAccontStore } from '@/stores/AccountStore.js'
import {
	resourceSchema,
	RESOURCE_NAMES,
	readResources,
	readResourceItem,
	createResourceItem,
	deleteResourceItem,
	convertToApiTimeDate,
	parseResource,
	formatResource,
} from '@/api/api.js'




export const rpcTaskSchema = Object.freeze(Object.assign(
	{},
	resourceSchema,
	{
		destinationStationId: Object.freeze({
			key:  'destination_station_id',
		}),
		destinationOrganizationId: Object.freeze({
			key:  'destination_organization_id',
		}),
		senderId: Object.freeze({
			key:  'sender_id',
		}),
		job: Object.freeze({
			key: 'job',
			title: '',
			allowed: Object.freeze({
				// Station manage RPCs
				//
				measurePotentialOnOffStation: {
					key: 'measure_potential_on_off_station',
					title: 'вимірювання потенціалу на вимкненній СКЗ',
				},
				doSurveyNow: {
					key: 'do_survey_now',
					title: 'опитування станції',
				},
				doElectricMeterSurveyNow: {
					key: 'do_electric_meter_survey_now',
					title: 'опитування електролічильника',
				},
				updateStabilizationParms: {
					key: 'update_stabilization_parms',
					title: 'установка стабілізації',
				},
				setPowerCounterStartValue: {
					key: 'set_power_counter_start_value',
					title: 'установка лічильника',
				},
				setPowerUnitState: {
					key: 'set_power_unit_state',
					title: 'запуск / зупинка силового модуля станції',
				},
				setUnitRegisters: {
					key: 'set_unit_registers',
					title: 'запис регістрів модуля',
				},
				setServiceUnitClock: {
					key: 'set_service_unit_clock',
					title: 'запис часу сервісного модуля',
				},

				// Station (organization) report RPCs
				//
				buildStationReport: {
					key: 'build_station_report',
					title: 'звіт по станції',
				},
				buildOrganizationReport: {
					key: 'build_organization_report',
					title: 'звіт по організації',
				},
			}),

			findJob(key) {
				return Object.values(rpcTaskSchema.job.allowed)
					.find((job) => job.key === key)
			},
		}),
		state: Object.freeze({
			key:  'state',
			title: '',
			allowed: Object.freeze({
				created: Object.freeze({
					key: 'created',
					title: 'завдання створено',
				}),

				processing: Object.freeze({
					key: 'processing',
					title: 'виконується',
				}),

				completeSuccess: Object.freeze({
					key: 'complete_success',
					title: 'виконано',
				}),

				waitPotentialStabilization: Object.freeze({
					key: 'wait_potential_stabilization',
					title: 'стабілізація потенціалу',
				}),

				tryingAgain: Object.freeze({
					key: 'trying_again',
					title: 'повторна спроба',
				}),

				notImplemented: Object.freeze({
					key: 'error_not_implemented',
					title: 'неможливий запит',
				}),

				errorAppLogic: Object.freeze({
					key: 'error_app_logic',
					title: 'внутрішня помилка додатка',
				}),

				noModules: Object.freeze({
					key: 'error_no_modules',
					title: 'відсутні модулі станції',
				}),

				errorNoStabilizationParmsSet: Object.freeze({
					key: 'error_no_stabilization_parms_set',
					title: 'немає встановлених параметрів стабілізації',
				}),

				noConnectionToModule: Object.freeze({
					key: 'error_no_connection_to_module',
					title: 'відсутній зв\'язок з модулем',
				}),

				errorPowerUnitTurnOff: Object.freeze({
					key: 'error_power_unit_turn_off',
					title: 'помилка при вимкненні силового модуля',
				}),

				errorPowerUnitTurnOn: Object.freeze({
					key: 'error_power_unit_turn_on',
					title: 'помилка при увімкненні силового модуля',
				}),

				errorReadPotential: Object.freeze({
					key: 'error_read_potential',
					title: 'помилка при зчитуванні потенціала',
				}),

				errorValueMismatch:  Object.freeze({
					key: 'error_value_mismatch',
					title: 'записане значення не співпадає при перевірці',
				}),

				errorParseReportTask: Object.freeze({
					key: 'error_parse_report_task',
					title: 'помилка розшифровки завдання звіту',
				}),

				errorCreateReport: Object.freeze({
					key: 'error_create_report',
					title: 'помилка створення звіту',
				}),

				errorWriteReport: Object.freeze({
					key: 'error_write_report',
					title: 'помилка збереження звіту',
				}),
			}),

			findState(key) {
				return Object.values(rpcTaskSchema.state.allowed)
					.find((state) => state.key === key)
			},

			isErorrState(key) {
				return key ? key.startsWith('error_') : false
			},

			isSuccessState(key) {
				return key == 'complete_success'
			},
		}),
		isComplete: Object.freeze({
			key:  'complete',
		}),
		context: Object.freeze({
			key:  'context',

			// For job == 'update_stabilization_parms'
			parameter: Object.freeze({
				key: 'parameter',

				allowed: Object.freeze({
					Eout: Object.freeze({
						key: 'Eout',
					}),
					Iout: Object.freeze({
						key: 'Iout',
					}),
					Uout: Object.freeze({
						key: 'Uout',
					}),
				}),
			}),
			value:Object.freeze({
				key: 'value',
			}),

			// For reports
			// job is buildStationReport or buildOrganizationReport
			//
			from: Object.freeze({
				key: 'from',
			}),
			to: Object.freeze({
				key: 'to',
			}),
			writer: Object.freeze({
				key: 'writer',
				id: Object.freeze({
					key: 'id',
				}),
				name: Object.freeze({
					key: 'name',
				}),
				organization: Object.freeze({
					key: 'organization',
					id: Object.freeze({
						key: 'id',
					}),
					name: Object.freeze({
						key: 'name',
					}),
				}),
			}),

			isAllOrganizations: Object.freeze({
				key: 'is_all_organizations',
			}),

			reportId: Object.freeze({
				key: 'report_id',
			}),

			// For job == 'set_power_counter_start_value'
			// 'value' - define in 'update_stabilization_parms' job
			prevValue: Object.freeze({
				key: 'prevValue',
			}),

			// For job == 'set_power_unit_state'abs
			state: Object.freeze({
				key: 'state',
			}),

			// For job == 'set_unit_registers'
			unit: Object.freeze({
				key: 'unit',

				allowed: Object.freeze({
					modemUnit: Object.freeze({
						key: 'modem_unit',
						title: '',
					}),
					serviceUnit: Object.freeze({
						key: 'service_unit',
						title: '',
					}),
					breaker: Object.freeze({
						key: 'breaker',
						title: '',
					}),
				}),
			}),
			settings: Object.freeze({
				key: 'settings',
			}),
			// 'writer' - define in 'build_station_report' job

			// For job == 'set_service_unit_clock'
			targetTime: Object.freeze({
				key: 'target_time',
			}),
		}),
	},
))

// Jobs list for manage station section
export const stationManageJobs = Object.freeze([
	rpcTaskSchema.job.allowed.measurePotentialOnOffStation.key,
	rpcTaskSchema.job.allowed.doSurveyNow.key,
	rpcTaskSchema.job.allowed.doElectricMeterSurveyNow.key,
	rpcTaskSchema.job.allowed.updateStabilizationParms.key,
	rpcTaskSchema.job.allowed.setPowerCounterStartValue.key,
	rpcTaskSchema.job.allowed.setPowerUnitState.key,
	rpcTaskSchema.job.allowed.setUnitRegisters.key,
	rpcTaskSchema.job.allowed.setServiceUnitClock.key,
])




// "Date" type pasport fields (flatten object keys)
const RPC_DATE_PATHS = [
	rpcTaskSchema.updateTime.key,
	rpcTaskSchema.createTime.key,
	`${rpcTaskSchema.context.key}.${rpcTaskSchema.context.from.key}`,
	`${rpcTaskSchema.context.key}.${rpcTaskSchema.context.to.key}`,
	`${rpcTaskSchema.context.key}.${rpcTaskSchema.context.targetTime.key}`,
]




export function useRpcTasks(filters) {
	const accountStore = useAccontStore()
	// Reactive lower bound for RPCs fetch time window.
	const fromTime = ref(null)
	// Reactive upper bound for RPCs fetch time window.
	const toTime = ref(null)
	// Sender filter - user id
	const senderId = ref(null)
	// Destination filter - station ids array
	const destinationStationIds = ref(null)
	// Destination filter - organnization id
	const destinationOrganizationId = ref(null)
	// State filter
	const state = ref(null)
	// Is complete RPC filter
	const isComplete = ref(null)
	// Job filter
	const jobs = ref(null)
	
	setFilters(filters)

	// Set request filters
	function setFilters(filters={
		fromTime: null,
		toTime: null,
		senderId: null,
		destinationStationIds: null,
		destinationOrganizationId: null,
		state: null,
		isComplete: null,
		jobs: null,
	}) {
		// console.log('setFilters', filters)
		if (filters.fromTime != null && filters.fromTime instanceof Date) {
			fromTime.value = new Date(filters.fromTime)
		}

		if (filters.toTime != null && filters.toTime instanceof Date) {
			toTime.value = new Date(filters.toTime)
		}

		if (filters.senderId != null && typeof filters.senderId === 'string') {
			senderId.value = filters.senderId
		}

		if (filters.destinationStationIds != null && Array.isArray(filters.destinationStationIds)) {
			destinationStationIds.value = filters.destinationStationIds
		}

		if (filters.destinationOrganizationId != null && typeof filters.destinationOrganizationId === 'string') {
			destinationOrganizationId.value = filters.destinationOrganizationId
		}

		if (filters.state != null && typeof filters.state === 'string') {
			state.value = filters.state
		}

		if (filters.isComplete != null && typeof filters.isComplete === 'boolean') {
			isComplete.value = filters.isComplete
		}

		if (filters.jobs != null && Array.isArray(filters.jobs)) {
			jobs.value = filters.jobs
		}
	}

	async function createRpc(
		senderId,
		job,
		destinationStationId=null,
		destinationOrganizationId=null,
		context=null,
		onSuccess,
		onError,
	) {
		const payload = {
			sender_id: senderId,
			job: job,
			state: 'created',
			complete: false,
			...(destinationStationId != null && { [rpcTaskSchema.destinationStationId.key]: destinationStationId }),
			...(destinationOrganizationId != null && { [rpcTaskSchema.destinationOrganizationId.key]: destinationOrganizationId }),
			...(context != null && { [rpcTaskSchema.context.key]: context }),
		}
	
		try {
			const rpcId = await createResourceItem(
				RESOURCE_NAMES.rpc,
				accountStore.userLogin,
				accountStore.userPassword,
				formatResource(payload, RPC_DATE_PATHS),
			)

			invalidateRpcQueries()

			if (onSuccess) {
				onSuccess()
			}

			return rpcId
		} catch (err) {
			console.error("on create RPC request", err)
			if (onError) {
				onError(err)
			}

			return null
		}
	}

	const rpcQuery = useQuery({
		// Include auth identity and time filter in key to auto-refetch on change
		queryKey: computed(() => [
			RESOURCE_NAMES.rpc,
			convertToApiTimeDate(fromTime.value),
			convertToApiTimeDate(toTime.value),
			senderId.value,
			destinationStationIds.value,
			destinationOrganizationId.value,
			state.value,
			isComplete.value,
			jobs.value,
		]),
		queryFn: async () => {
			const isTimeRange = fromTime.value !== null || toTime.value !== null
			let isWhereInArgs = false
			const whereObject = {}

			if (isTimeRange) {
				isWhereInArgs = true
				whereObject[rpcTaskSchema.createTime.key] = {
					...(fromTime.value !== null && { "$gt": convertToApiTimeDate(fromTime.value) }),
					...(toTime.value !== null && { "$lt": convertToApiTimeDate(toTime.value) }),
				}
			}

			if (senderId.value !== null) {
				isWhereInArgs = true
				whereObject[rpcTaskSchema.senderId.key] = senderId.value
			}

			if (destinationStationIds.value !== null) {
				isWhereInArgs = true
				whereObject[rpcTaskSchema.destinationStationId.key] = {
					...({ "$in": destinationStationIds.value }),
				}
			}

			if (destinationOrganizationId.value !== null) {
				isWhereInArgs = true
				whereObject[rpcTaskSchema.destinationOrganizationId.key] = destinationOrganizationId.value
			}

			if (state.value !== null) {
				isWhereInArgs = true
				whereObject[rpcTaskSchema.state.key] = state.value
			}

			if (isComplete.value !== null) {
				isWhereInArgs = true
				whereObject[rpcTaskSchema.isComplete.key] = isComplete.value
			}

			if (jobs.value !== null) {
				isWhereInArgs = true
				whereObject[rpcTaskSchema.job.key] = {
					...({ "$in": jobs.value }),
				}
			}
			
			const args = [
				...(isWhereInArgs ? [`where=${JSON.stringify(whereObject)}`] : [])
			]
			// console.log('fetch args', args)
			const res = await readResources(
				RESOURCE_NAMES.rpc,
				accountStore.userLogin,
				accountStore.userPassword,
				args,
			)

			return res.map((el) => parseResource(el, RPC_DATE_PATHS))
		},
	})

	const allRpcs = computed(() => rpcQuery.data.value ?? [])

	/**
	 * Read one RPC
	 * @param {ResourceId} taskId 
	 * @returns 
	 */
	async function readRpc(taskId) {
		return await readResourceItem(
			RESOURCE_NAMES.rpc,
			taskId,
			accountStore.userLogin,
			accountStore.userPassword,
		).then(response => {
			return parseResource(response, RPC_DATE_PATHS)
		}).catch(() => {
			return undefined
		})
	}

	async function deleteRpc(rpc, onSuccess, onError) {
		try {
			const result = await deleteResourceItem(
				RESOURCE_NAMES.rpc,
				accountStore.userLogin,
				accountStore.userPassword,
				rpc[rpcTaskSchema.id.key],
				rpc[rpcTaskSchema.etag.key],
			)
			
			if (!result) {
				throw new Error(`Delete RPC request status: ${result._status}`)
			}

			invalidateRpcQueries()

			if (onSuccess) {
				onSuccess()
			}

			return true
		} catch (err) {
			console.error("on delete RPC request", err)
			if (onError) {
				onError(err)
			}

			return false
		}
	}

	async function invalidateRpcQueries() {
		await queryClient.invalidateQueries({
			queryKey: [ RESOURCE_NAMES.rpc, ],
			exact: false, // inavalidate queries that begins from this key
		})
	}

	return {
		rpcTaskSchema,

		allRpcs,
		isLoading: rpcQuery.isLoading,
		isError: rpcQuery.isError,
		error: rpcQuery.error,
		refetch: rpcQuery.refetch,
		
		setFilters,
		createRpc,
		readRpc,
		deleteRpc,
	}
}